<template>
  <v-dialog
    :value="value"
    :content-class="contentClasses"
    :max-width="maxWidth"
    :width="width"
    :fullscreen="size === 'fullscreen'"
    :show-back-button="showBackButton"
    :persistent="persistent"
    eager
    :transition="transition"
    @input="$emit('input', $event)"
    @keydown="onKeydown($event)"
  >
    <!-- Fullscreen Modal -->
    <div
      v-if="size === 'fullscreen'"
      class="sn-modal-fs sn-background-gray-01 d-flex flex-column overflow-y-auto"
      :data-cy="dataCy"
    >
      <div
        class="d-flex flex-row"
        style="z-index: 1001;"
      >
        <div class="modal-title mt-6 mr-6 ml-6">
          <h1
            class="sn-h1 pt-6 pb-4"
            data-cy="modal-title"
          >
            {{ title }}
          </h1>
          <h2
            class="sn-body-1"
            data-cy="modal-subtitle"
          >
            {{ subTitle }}
          </h2>
        </div>
        <div
          class="modal-title-button mt-6 ml-6"
          :style="{ visibility: showBackButton ? 'visible' : 'hidden' }"
          @click="back"
        >
          <v-icon data-cy="back-modal-btn">
            $snArrowLeft
          </v-icon>
          <span>Back</span>
        </div>
        <div
          class="modal-title-button mt-6 mr-12"
          style="right: 0;"
          @click="close"
        >
          <v-icon
            data-cy="exit-modal-btn"
          >
            $snExit
          </v-icon>
        </div>
      </div>

      <div class="d-flex flex-row justify-center">
        <!-- Fullscreen modal with table slot -->
        <v-card
          v-if="$slots.table"
          width="1063px"
          class="sn-modal-fs--container d-flex flex-column"
        >
          <v-card-text
            data-cy="modal-text"
            class="px-8"
          >
            <slot name="table" />
          </v-card-text>
        </v-card>

        <!-- Fullscreen modal "blank slate" slot -->
        <v-card-text
          v-else
          ref="modalBody"
          class="sn-body-1 px-8 py-4"
          data-cy="modal-text"
        >
          <slot />
        </v-card-text>
      </div>
    </div>

    <!-- Standard Modal -->
    <v-card
      v-else
      class="d-flex flex-column"
      :min-width="$vuetify.breakpoint.smAndDown ? '475px' : '600px'"
      :data-cy="dataCy"
      :max-height="maxHeight"
    >
      <v-card-title
        class="sn-modal-title sn-h3 sn-background-gray-01 px-8 pt-6 pb-4"
        data-cy="modal-title"
      >
        {{ title }}
        <v-spacer />
        <v-icon
          v-if="type === 'dismiss'"
          type=""
          data-cy="exit-modal-btn"
          @click="close"
        >
          $snExit
        </v-icon>
      </v-card-title>

      <v-card-text
        ref="modalBody"
        class="sn-body-1 px-8 py-4 overflow-y-auto"
        data-cy="modal-text"
      >
        <slot />
      </v-card-text>

      <v-card-actions
        v-if="type !== 'dismiss'"
        :class="`px-6 py-6 ${bodyOverflow ? 'sn-background-gray-01' : ''}`"
      >
        <slot name="footer">
          <v-row
            align="center"
            no-gutters
          >
            <v-col class="mr-4">
              <slot name="preFooter">
                <v-row
                  v-if="warningText"
                  align="center"
                  class="sn-text-error sn-body-2"
                  data-cy="modal-warning"
                  no-gutters
                >
                  <v-col class="shrink pa-0 mr-2">
                    <v-icon
                      v-if="warningIcon"
                      color="snError"
                    >
                      {{ warningIcon }}
                    </v-icon>
                  </v-col>
                  <v-col class="pa-0">
                    {{ warningText }}
                  </v-col>
                </v-row>
                <v-row
                  v-else-if="preFooterText"
                  align="start"
                  class="sn-text-btn pl-5"
                  data-cy="modal-pre-footer-btn"
                >
                  <div>
                    <sn-btn
                      :text="preFooterText"
                      type="text"
                      :prepend-icon="preFooterIcon"
                      :icon-size="24"
                      @click="onPreFooter"
                    />
                  </div>
                </v-row>
              </slot>
            </v-col>
            <v-col cols="auto">
              <sn-btn
                v-if="type === 'decision'"
                :id="snInputProps_identifier ? snInputProps_identifier + '-' + secondaryText : undefined"
                :text="secondaryText"
                :size="buttonSize"
                class="mr-2"
                type="secondary"
                data-cy="modal-cancellation-btn"
                :data-pendo="snInputProps_pendo ? snInputProps_pendo + '-' + secondaryText : undefined"
                :prepend-icon="secondaryIcon"
                @click="close"
              />
              <sn-btn
                :id="snInputProps_identifier ? snInputProps_identifier + '-' + primaryText : undefined"
                :text="primaryText"
                :size="buttonSize"
                class="mx-0"
                :warning="warningButton"
                :disabled="disabled"
                :loading="loading"
                :text-case="primaryTextCase"
                data-cy="modal-confirmation-btn"
                :data-pendo="snInputProps_pendo ? snInputProps_pendo + '-' + primaryText : undefined"
                :prepend-icon="icon"
                @click="submit"
              />
            </v-col>
          </v-row>
        </slot>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import SnBtn from '~/components/snui/v2/snBtn/snBtn.vue'
import SnInputProps from '../props/SnInputProps'
import { cypressMixin } from '../mixins/cypressMixin'
import { ResizeObserver } from 'resize-observer'

export default {
  name: 'WebModal',
  components: {
    SnBtn
  },
  mixins: [cypressMixin],
  props: {
    ...SnInputProps,
    value: {
      type: Boolean,
      required: true
    },
    type: {
      type: String,
      default: 'confirmation',
      validator (type) {
        return ['confirmation', 'decision', 'dismiss'].includes(type)
      }
    },
    buttonSize: {
      type: String,
      default: 'standard',
      validator (buttonSize) {
        return ['small', 'standard', 'large', 'x-large', 'full-width'].includes(buttonSize)
      }
    },
    size: {
      type: String,
      default: 'standard',
      validator (size) {
        return ['standard', 'large', 'fullscreen'].includes(size)
      }
    },
    title: {
      type: String,
      default: 'Modal'
    },
    subTitle: {
      type: String,
      default: ''
    },
    primaryText: {
      type: String,
      default: 'Okay'
    },
    primaryTextCase: {
      type: String,
      default: 'sentence',
      required: false,
      validator (primaryTextCase) {
        return ['uppercase', 'title', 'sentence', ''].includes(primaryTextCase)
      }
    },
    secondaryText: {
      type: String,
      default: 'Cancel'
    },
    warningText: {
      type: String,
      default: ''
    },
    warningIcon: {
      type: String,
      default: ''
    },
    warningButton: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    icon: {
      type: String,
      default: ''
    },
    secondaryIcon: {
      type: String,
      default: null
    },
    contentClass: {
      type: String,
      default: ''
    },
    showBackButton: {
      type: Boolean,
      default: false
    },
    persistent: {
      type: Boolean,
      default: false
    },
    transition: {
      type: String,
      default: 'dialog-transition',
      required: false
    },
    keydownSubmit: {
      type: Boolean,
      default: false,
      required: false
    },
    preFooterText: {
      type: String,
      default: ''
    },
    preFooterIcon: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      bodyOverflow: false
    }
  },

  computed: {
    width () {
      if (this.size === 'standard') {
        return 'unset'
      } else {
        return this.$vuetify.breakpoint.lgAndUp ? '70%' : '80%'
      }
    },
    maxWidth () {
      if (this.size === 'large') {
        return '950px'
      } else {
        return '600px'
      }
    },
    maxHeight () {
      return '720px'
    },
    contentClasses () {
      const classes = ['sn-modal']

      if (this.size !== 'fullscreen') {
        classes.push('mx-8')
      }

      if (this.contentClass) {
        classes.push(this.contentClass)
      }

      return classes.join(' ')
    }
  },

  mounted () {
    if (this.$refs?.modalBody) {
      this.observeHeight()
    }
  },

  methods: {
    onKeydown (event) {
      if (this.keydownSubmit && event.keyCode === 13) {
        this.submit()
      }
    },
    close () {
      this.$emit('close')
      this.$emit('secondary-submit')
    },
    submit () {
      this.$emit('submit')
    },
    getOverflow () {
      this.$nextTick(() => {
        this.$nextTick(() => {
          const body = this.$refs?.modalBody
          this.bodyOverflow = body?.clientHeight < body?.scrollHeight
        })
      })
    },
    back () {
      this.$emit('back')
    },
    observeHeight () {
      const vueInstance = this
      const resizeObserver = new ResizeObserver(function () {
        vueInstance.getOverflow()
      })
      resizeObserver.observe(this.$refs?.modalBody)
    },
    onPreFooter () {
      this.$emit('pre-footer-click')
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui';

::v-deep {
  z-index: 1000;
  .sn-modal {
    display: flex;

    &:not(.v-dialog--fullscreen) {
      border-radius: 16px;
    }

    /*** Fullscreen styles ***/
    &.v-dialog--fullscreen .sn-modal-fs {
      &--container {
        max-height: calc(100vh - 120px);
        border-radius: 16px !important;

        .v-data-table__wrapper {
          max-height: calc(100vh - 370px);
          overflow: auto;

          .v-data-table-header .text-start {
            box-shadow: none !important;
          }
        }
      }
      h1 { text-align: center; }
      h2 { text-align: center; }
    }
    /*** End fullscreen styles ***/

    &-title {
      word-break: break-word;
    }

    .sn-h3 {
      font-size: 26px !important;
      font-weight: $font-weight-semi-bold !important;

      .v-icon {
        color: $sn-primary-03;
      }
    }
  }
}

@media (max-width: 979px) {
  ::v-deep .sn-modal {
    margin: 0 !important;
    max-height: 100% !important;
    border-radius: 0;
    max-width: 100% !important;
    width: 100% !important;
    overflow: auto;

    .v-card {
      max-height: 100% !important;
      min-width: 100% !important;

      .v-card__title {
        width: 100%;

        .v-icon {
          -webkit-appearance: none;
        }
      }
      .v-card__text {
        width: 100%;
      }
    }
  }
}
.modal-title-button {
  font-family: $font-family-primary;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 64px;
  text-align: center;
  align-self: center;
  position: fixed;
  span {
    font-weight: 600;
  }
}

.modal-title {
  flex-grow: 1;
}
</style>
